import { useMemo, useState } from "react";
import {
  adjustStockInputSchema,
  type AdjustStockReason,
  type Product,
  type StockMovement,
} from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { Modal } from "./Modal";
import { ErrorBanner } from "./ErrorBanner";

const REASONS: { value: AdjustStockReason; label: string; hint: string }[] = [
  { value: "receive", label: "Receive", hint: "Stock received — enter a positive quantity." },
  { value: "return", label: "Return", hint: "Customer return — enter a positive quantity." },
  { value: "sell", label: "Sell", hint: "Sold or shipped — enter a negative quantity." },
  { value: "adjust", label: "Adjust", hint: "Manual adjustment — positive or negative." },
  { value: "correction", label: "Correction", hint: "Fix a counting error — positive or negative." },
];

interface StockAdjustModalProps {
  product: Pick<Product, "id" | "name" | "quantity">;
  onClose: () => void;
  onAdjusted: (result: { product: Product; movement: StockMovement }) => void;
}

export function StockAdjustModal({ product, onClose, onAdjusted }: StockAdjustModalProps) {
  const api = useApi();
  const [reason, setReason] = useState<AdjustStockReason>("receive");
  const [deltaStr, setDeltaStr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const hint = REASONS.find((r) => r.value === reason)?.hint ?? "";
  const delta = Number(deltaStr);
  const deltaValid = deltaStr.trim() !== "" && Number.isInteger(delta);
  const projected = useMemo(
    () => (deltaValid ? product.quantity + delta : product.quantity),
    [deltaValid, product.quantity, delta],
  );

  const submit = async () => {
    setError(undefined);
    if (!deltaValid) {
      setError("Enter a whole-number quantity to add or remove.");
      return;
    }
    if (projected < 0) {
      setError("Insufficient stock — this would drop the quantity below zero.");
      return;
    }
    const input = { delta, reason, note: note.trim() ? note.trim() : undefined };
    const parsed = adjustStockInputSchema.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid adjustment.");
      return;
    }

    setBusy(true);
    try {
      const result = await api.adjustStock(product.id, parsed.data);
      onAdjusted(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Adjustment failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Adjust stock — ${product.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Working…" : "Apply adjustment"}
          </button>
        </>
      }
    >
      {error ? <ErrorBanner title="Adjustment failed" message={error} /> : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="form-field">
          <label className="form-field__label" htmlFor="adjust-reason">
            Reason
          </label>
          <select
            id="adjust-reason"
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value as AdjustStockReason)}
          >
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="adjust-delta">
            Change in quantity
          </label>
          <input
            id="adjust-delta"
            className="input"
            type="number"
            step={1}
            inputMode="numeric"
            value={deltaStr}
            onChange={(e) => setDeltaStr(e.target.value)}
            placeholder="e.g. 10 or -3"
          />
          <span className="form-hint muted">{hint}</span>
        </div>

        <div className="form-field">
          <label className="form-field__label" htmlFor="adjust-note">
            Note <span className="muted">(optional)</span>
          </label>
          <textarea
            id="adjust-note"
            className="input textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
          />
        </div>

        <p className="muted form-hint">
          On-hand: {product.quantity} → <strong>{projected}</strong>
        </p>
      </form>
    </Modal>
  );
}
